import React, { useEffect, useRef } from 'react';

// Turnstile.js — renders the Cloudflare Turnstile widget and hands the solved
// token back to the parent. The token is passed to Cognito signUp as
// clientMetadata, where the PreSignUp Lambda verifies it server-side before any
// user is created or verification email sent.
//
// Loads the Cloudflare script directly rather than pulling in a wrapper
// dependency. The script is fetched once per page regardless of how many
// widgets mount.

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

function loadTurnstileScript() {
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null; // allow a retry on remount
      reject(new Error('unable to load Turnstile'));
    };

    document.head.appendChild(script);
  });

  return scriptPromise;
}

// resetSignal: increment to discard the current token and render a fresh
// challenge. Turnstile tokens are single-use, so a failed submit must reset or
// the retry will be rejected as already-redeemed.
//
// theme defaults to 'light' rather than Turnstile's 'auto': the auth forms are
// always light, so 'auto' renders a dark widget for anyone whose OS is in dark
// mode. size 'flexible' fills the container instead of the fixed 300px, so the
// widget lines up with the full-width inputs.
const Turnstile = ({
  siteKey,
  onToken,
  resetSignal = 0,
  theme = 'light',
  size = 'flexible',
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Held in a ref so a changing callback identity does not tear down and
  // re-render the widget on every parent render.
  const onTokenRef = useRef(onToken);
  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    if (!siteKey) {
      return undefined;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          callback: (token) => onTokenRef.current(token),
          'error-callback': () => onTokenRef.current(''),
          'expired-callback': () => onTokenRef.current(''),
        });
      })
      .catch(() => {
        // Script blocked or offline. Clearing the token keeps the form honest:
        // the user cannot submit something the server would reject anyway.
        onTokenRef.current('');
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, size]);

  useEffect(() => {
    if (resetSignal > 0 && widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      onTokenRef.current('');
    }
  }, [resetSignal]);

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} />;
};

export default Turnstile;
